import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Switch } from '@/_shared/components/ui/switch';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

export function Settings(): React.ReactElement {
  usePageTitle('Settings');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isOwner, isAdmin, workspace } = useWorkspace();
  const { workspaces } = useAuth();
  const orpc = useOrpc();
  const [localWorkspaceId, setLocalWorkspaceId] = useState('');
  const [localPat, setLocalPat] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [newSlug, setNewSlug] = useState('');

  const { data, isLoading } = useQuery(orpc.settings.get.queryOptions({ input: {} }));

  // Sync server credentials into local form state (render-phase adjustment).
  // Field-scoped so unrelated cache updates (e.g. the public-sharing toggle)
  // never overwrite a user's unsaved credential edits.
  const [prevWorkspaceId, setPrevWorkspaceId] = useState(data?.settings?.rudderstackWorkspaceId);
  const [prevPat, setPrevPat] = useState(data?.settings?.rudderstackPat);
  if (data?.settings) {
    if (data.settings.rudderstackWorkspaceId !== prevWorkspaceId) {
      setPrevWorkspaceId(data.settings.rudderstackWorkspaceId);
      setLocalWorkspaceId(data.settings.rudderstackWorkspaceId ?? '');
    }
    if (data.settings.rudderstackPat !== prevPat) {
      setPrevPat(data.settings.rudderstackPat);
      setLocalPat(data.settings.rudderstackPat ?? '');
    }
  }

  const testMutation = useMutation(
    orpc.settings.testRudderstack.mutationOptions({
      onSuccess: (result) => {
        if (result.success) {
          toast.success(`RudderStack connected — ${result.sourceCount ?? 0} source(s) found`);
        } else {
          toast.error(result.error ?? 'RudderStack connection test failed');
        }
      },
      onError: () => {
        toast.error('Network error testing RudderStack');
      },
    }),
  );

  const saveMutation = useMutation(
    orpc.settings.update.mutationOptions({
      onSuccess: (result) => {
        if (result.settings) {
          queryClient.setQueryData(orpc.settings.get.queryOptions({ input: {} }).queryKey, {
            settings: result.settings,
          });
        }
        toast.success('Settings saved successfully');
        // Auto-run test after save
        testMutation.mutate({
          workspaceId: localWorkspaceId || undefined,
          pat: localPat || undefined,
        });
      },
      onError: () => {
        toast.error('Network error saving settings');
      },
    }),
  );

  const publicSharingMutation = useMutation(
    orpc.settings.update.mutationOptions({
      onSuccess: (result) => {
        if (result.settings) {
          queryClient.setQueryData(orpc.settings.get.queryOptions({ input: {} }).queryKey, {
            settings: result.settings,
          });
        }
      },
      onError: () => {
        toast.error('Failed to update public sharing setting');
      },
    }),
  );

  const renameMutation = useMutation(
    orpc.workspace.rename.mutationOptions({
      onSuccess: (result) => {
        toast.success('Workspace renamed');
        void queryClient.cancelQueries();
        void queryClient.invalidateQueries();
        void navigate(`/w/${result.newSlug}/settings`);
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to rename workspace');
      },
    }),
  );

  const deleteMutation = useMutation(
    orpc.workspace.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Workspace deleted');
        // Cancel in-flight queries first to prevent 404 toasts from stale workspace queries
        void queryClient.cancelQueries();
        void queryClient.invalidateQueries();
        const other = workspaces.find((w) => w.id !== workspace.id);
        void navigate(other ? `/w/${other.id}` : '/new-workspace');
      },
      onError: () => {
        toast.error('Failed to delete workspace');
      },
    }),
  );

  const handleRename = useCallback(() => {
    if (!newSlug || newSlug === workspace.id) return;
    renameMutation.mutate({ newSlug });
  }, [newSlug, workspace.id, renameMutation]);

  const handleDelete = useCallback(() => {
    if (deleteConfirm !== workspace.id) return;
    deleteMutation.mutate({});
  }, [deleteConfirm, workspace.id, deleteMutation]);

  const handleTestRudderstack = useCallback(() => {
    // Send form values if present; backend falls back to saved credentials for redacted/missing values
    testMutation.mutate({
      workspaceId: localWorkspaceId || undefined,
      pat: localPat || undefined,
    });
  }, [testMutation, localWorkspaceId, localPat]);

  const handleSave = useCallback(() => {
    saveMutation.mutate({
      rudderstackWorkspaceId: localWorkspaceId || null,
      rudderstackPat: localPat || null,
    });
  }, [saveMutation, localWorkspaceId, localPat]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <LoaderCircle size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <>
      {!isAdmin && (
        <div className="mb-4 rounded border border-amber-200/50 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Only workspace admins can modify settings.
        </div>
      )}
      {/* RudderStack credentials */}
      <section className="mb-8 rounded border border-border bg-card p-6">
        <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">
          RudderStack Connection
        </h2>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm text-muted-foreground">Workspace ID</label>
            <input
              type="text"
              value={localWorkspaceId}
              onChange={(e) => setLocalWorkspaceId(e.target.value)}
              placeholder="workspace_..."
              disabled={!isAdmin}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-brand disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Find your workspace ID at{' '}
              <a
                href="https://app.rudderstack.com/workspace"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand hover:underline"
              >
                app.rudderstack.com/workspace
              </a>
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm text-muted-foreground">Personal Access Token</label>
            <input
              type="password"
              value={localPat}
              onChange={(e) => setLocalPat(e.target.value)}
              placeholder="pat_..."
              disabled={!isAdmin}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-brand disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Generate at{' '}
              <a
                href="https://app.rudderstack.com/profile"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand hover:underline"
              >
                app.rudderstack.com/profile
              </a>{' '}
              &rarr; Personal Access Tokens
            </p>
          </div>
        </div>
        <button
          onClick={handleTestRudderstack}
          disabled={!isAdmin || testMutation.isPending}
          className="mt-4 inline-flex items-center gap-2 rounded border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-brand hover:text-brand disabled:opacity-30"
        >
          {testMutation.isPending && <LoaderCircle size={14} className="animate-spin" />}
          Test Connection
        </button>
      </section>

      {/* Save Button */}
      <div className="mb-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={!isAdmin || saveMutation.isPending}
          className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-6 py-2 text-sm text-brand transition-colors hover:bg-brand/20 disabled:opacity-50"
        >
          {saveMutation.isPending && <LoaderCircle size={14} className="animate-spin" />}
          Save Settings
        </button>
      </div>

      {/* Public sharing — admin only */}
      {isAdmin && data?.settings && (
        <section className="mb-8 rounded border border-border bg-card p-6">
          <h2 className="mb-4 text-sm font-bold tracking-wider text-muted-foreground uppercase">Public sharing</h2>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <label htmlFor="public-sharing-toggle" className="text-sm font-medium">
                Allow public dashboard links
              </label>
              <p className="text-xs text-muted-foreground">
                When on, members can publish dashboards to a URL accessible without sign-in. Turning this off
                immediately makes existing public links unavailable until you re-enable it.
              </p>
            </div>
            <Switch
              id="public-sharing-toggle"
              checked={data.settings.publicSharingEnabled}
              disabled={publicSharingMutation.isPending}
              onCheckedChange={(checked) => publicSharingMutation.mutate({ publicSharingEnabled: checked })}
              aria-label="Allow public dashboard links"
            />
          </div>
        </section>
      )}

      {/* Danger Zone — owner only */}
      {isOwner && workspace && (
        <section className="space-y-6 rounded border border-red-300/50 bg-red-50/50 p-6">
          <h2 className="text-sm font-bold tracking-wider text-red-600 uppercase">Danger Zone</h2>

          {/* Rename workspace */}
          <div>
            <h3 className="mb-1 text-sm font-semibold text-red-600">Rename workspace</h3>
            <p className="mb-3 text-sm text-red-600/80">
              Changing the slug will update all URLs. Existing links will stop working.
            </p>
            <div className="flex items-end gap-3">
              <div className="max-w-xs flex-1">
                <label className="mb-1 block text-sm text-red-600/80">New slug</label>
                <input
                  type="text"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder={workspace.id}
                  className="w-full rounded border border-red-300/50 bg-white px-3 py-2 text-sm text-foreground outline-none placeholder:text-red-300 focus:border-red-400"
                />
              </div>
              <button
                onClick={handleRename}
                disabled={!newSlug || newSlug === workspace.id || renameMutation.isPending}
                className="inline-flex items-center gap-2 rounded border border-red-400 bg-white px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
              >
                {renameMutation.isPending && <LoaderCircle size={14} className="animate-spin" />}
                Rename
              </button>
            </div>
          </div>

          <hr className="border-red-200/50" />

          {/* Delete workspace */}
          <div>
            <h3 className="mb-1 text-sm font-semibold text-red-600">Delete workspace</h3>
            <p className="mb-3 text-sm text-red-600/80">
              This removes all dashboards, workflows, chat history, and settings permanently.
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm text-red-600/80">
                  Type <span className="font-mono font-semibold">{workspace.id}</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder={workspace.id}
                  className="w-full max-w-xs rounded border border-red-300/50 bg-white px-3 py-2 text-sm text-foreground outline-none placeholder:text-red-300 focus:border-red-400"
                />
              </div>
              <button
                onClick={handleDelete}
                disabled={deleteConfirm !== workspace.id || deleteMutation.isPending}
                className="inline-flex items-center gap-2 rounded border border-red-400 bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-30"
              >
                {deleteMutation.isPending && <LoaderCircle size={14} className="animate-spin" />}
                Delete Workspace
              </button>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
